import { useState } from "react";

import { ChevronDown } from "lucide-react";
import { NavLink } from "react-router-dom";

import {
  type NavLink as NavLinkConfig,
  navEntries,
} from "@/components/layout/nav-config";
import { useAuth } from "@/features/auth/auth-provider";
import { useI18n } from "@/lib/i18n/i18n-provider";
import { cn } from "@/lib/utils";

interface NavContentProps {
  collapsed?: boolean;
  onNavigate?: () => void;
}

/** Same fail-open rule as usePermission() (features/auth/use-permission.ts) — inlined here
 * instead of calling that hook because this runs inside an Array.filter callback, where calling
 * a hook per-iteration would violate the rules of hooks. `keys` itself still comes from
 * useAuth() at the top of NavContent, same as the hook does internally. A link is visible when
 * the user's permission groups grant `${permissionKey}.view` — the exact same grant that gates
 * the page/API server-side, so the sidebar now shows exactly the pages a user can actually open. */
function canSeeLink(link: NavLinkConfig, permissionKeys: string[] | undefined): boolean {
  return (
    permissionKeys === undefined || permissionKeys.includes(`${link.permissionKey}.view`)
  );
}

function NavItem({
  item,
  collapsed,
  onNavigate,
  indent,
}: {
  item: NavLinkConfig;
  collapsed: boolean;
  onNavigate?: () => void;
  indent?: boolean;
}) {
  const { t } = useI18n();
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      title={collapsed ? t(item.labelKey) : undefined}
      className={({ isActive }) =>
        cn(
          "flex min-h-11 items-center gap-3 rounded-md px-2.5 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground md:min-h-9",
          collapsed && "justify-center",
          indent && !collapsed && "pl-8",
          isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
        )
      }
    >
      <item.icon className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
    </NavLink>
  );
}

export function NavContent({ collapsed = false, onNavigate }: NavContentProps) {
  const { t } = useI18n();
  const { user } = useAuth();
  const permissionKeys = user?.permissionKeys;
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const isGroupOpen = (key: string) => openGroups[key] ?? true;

  return (
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
      {navEntries
        .filter((entry) => entry.type !== "link" || canSeeLink(entry, permissionKeys))
        .map((entry) => {
          if (entry.type === "link") {
            return (
              <div key={entry.to}>
                <NavItem item={entry} collapsed={collapsed} onNavigate={onNavigate} />
              </div>
            );
          }

          const children = entry.children.filter((child) =>
            canSeeLink(child, permissionKeys),
          );
          if (children.length === 0) return null;
          const open = isGroupOpen(entry.labelKey);

          return (
            <div key={entry.labelKey} className="pt-2 first:pt-0">
              {collapsed ? (
                <div className="my-1 border-t border-sidebar-border" />
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    setOpenGroups((prev) => ({ ...prev, [entry.labelKey]: !open }))
                  }
                  className="flex w-full items-center justify-between px-2 py-1 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50 hover:text-sidebar-foreground/80"
                >
                  <span>{t(entry.labelKey)}</span>
                  <ChevronDown
                    className={cn("size-3.5 transition-transform", !open && "-rotate-90")}
                  />
                </button>
              )}
              {(collapsed || open) && (
                <ul className="mt-1 space-y-1">
                  {children.map((child) => (
                    <li key={child.to}>
                      <NavItem
                        item={child}
                        collapsed={collapsed}
                        onNavigate={onNavigate}
                        indent
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
    </nav>
  );
}
